import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { Practitioner, Location } from '../types';
import PractitionerCard from '../components/PractitionerCard';

interface PractitionerWithLocation extends Practitioner {
  locations: Location[];
}

export default function Search() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [practitioners, setPractitioners] = useState<PractitionerWithLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const searchPractitioners = async () => {
      if (!query.trim()) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Search by practitioner name
        const { data: nameResults, error: nameError } = await supabase
          .from('practitioners')
          .select(`
            *,
            locations (
              id,
              practitioner_id,
              address,
              city,
              state,
              zip,
              latitude,
              longitude
            ),
            specialties (*)
          `)
          .ilike('name', `%${query}%`);

        if (nameError) {
          throw new Error(`Database error: ${nameError.message}`);
        }

        // Search in locations table
        const { data: matchingLocations, error: locationsError } = await supabase
          .from('locations')
          .select('practitioner_id')
          .or(`city.ilike.%${query}%,state.ilike.%${query}%`);

        if (locationsError) {
          throw new Error(`Database error: ${locationsError.message}`);
        }

        // Get practitioners with matching locations
        let locationResults: PractitionerWithLocation[] = [];
        if (matchingLocations && matchingLocations.length > 0) {
          const practitionerIds = matchingLocations.map(loc => loc.practitioner_id);
          const { data: locationPractitioners, error: practitionersError } = await supabase
            .from('practitioners')
            .select(`
              *,
              locations (
                id,
                practitioner_id,
                address,
                city,
                state,
                zip,
                latitude,
                longitude
              ),
              specialties (*)
            `)
            .in('id', practitionerIds);

          if (practitionersError) {
            throw new Error(`Database error: ${practitionersError.message}`);
          }

          locationResults = locationPractitioners || [];
        }

        // Combine and deduplicate results
        const allResults = [...(nameResults || []), ...locationResults];
        const uniqueResults = allResults.filter((practitioner, index, self) =>
          index === self.findIndex((p) => p.id === practitioner.id)
        );

        setPractitioners(uniqueResults);
      } catch (err) {
        console.error('Search error:', err);
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('An unexpected error occurred while searching. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    searchPractitioners();
  }, [query]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
        Search Results for "{query}"
      </h1>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">Searching practitioners...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      ) : practitioners.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-300">
            No practitioners found for "{query}". Try searching with a different name or location.
          </p>
        </div>
      ) : (
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {practitioners.map((practitioner) => (
            practitioner.locations?.map((location) => (
              <PractitionerCard
                key={`${practitioner.id}-${location.id}`}
                practitioner={practitioner}
                location={location}
              />
            ))
          ))}
        </div>
      )}
    </div>
  );
}